import { Component, ElementRef, ViewChild } from '@angular/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { LaboratoryFileService } from 'src/app/services/laboratoryFile.service';
import { IAddLaboratoryFile } from 'src/app/interfaces/LaboratoryFile/IAddLaboratoryFile';
import { ILaboratoryFileDropdown } from 'src/app/interfaces/LaboratoryFile/ILaboratoryFileDropdown';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from 'src/app/services/swal.service';
import { dateRangeValidator } from 'src/date/date.configuration';
import { validateUpload } from 'src/app/guards/file.guard';

@Component({
  selector: 'app-laboratory-file-add',
  templateUrl: './laboratory-file-add.component.html',
  styleUrls: ['./laboratory-file-add.component.css'],
  standalone: false,
})
export class LaboratoryFileAddComponent {
  @ViewChild('fileInput', { static: false })
  fileInput?: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();

  laboratoryDropdown: ILaboratoryFileDropdown | undefined;

  fileObj?: File;

  isVisible = false;
  isLoading = false;

  isEdit = false;
  editProblemsId?: number;

  laboratorySubmitted = false;

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef; // Reference to the modal

  laboratoryForm: FormGroup = new FormGroup({});

  constructor(
    private readonly laboratoryService: LaboratoryFileService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.isEdit = false;

    this.laboratoryService.getLaboratoryDropdown().subscribe({
      next: (pro) => {
        this.laboratoryDropdown = pro?.data;
        this.isLoading = false;
      },
      error: (err) => {},
    });

    this.laboratoryForm = this.formBuilder.group({
      categoryValue: [null, Validators.required],
      examTitleValue: [null, Validators.required],
      dateValue: [
        null,
        [Validators.required, dateRangeValidator(undefined, new Date())],
      ],
      descriptionValue: [null, Validators.required],
      fileValue: [null, Validators.required],
    });

    this.laboratoryService.laboratoryEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        // GET DATA FIELDS AND ASSIGN DEFAULT VALUES TO FORM
        if (data) {
          this.isEdit = true;
          this.editProblemsId = data?.id;

          this.laboratoryForm.patchValue({
            categoryValue: data?.categoryId,
            examTitleValue: data?.examTitle,
            dateValue: data?.date,
            descriptionValue: data?.description,
          });
        } else {
          this.isEdit = false;
          this.editProblemsId = undefined;

          this?.laboratoryForm.reset();

          this.laboratoryForm.patchValue({
            dateValue: this?.getCurrentDate(),
          });
        }
      });

    this.laboratoryService.laboratoryModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });
  }

  ngOnDestroy() {
    // Unsubscribe from observables
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  getCurrentDate() {
    return new Date().toISOString().split('T')[0];
  }

  handleCancel() {
    this.laboratoryForm.reset();

    this.laboratoryService.laboratoryModalSub.next(false);

    this.laboratorySubmitted = false;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    this.isLoading = true;
    this.laboratorySubmitted = true;

    if (this.fileObj && this.laboratoryForm.valid) {
      const laboratoryObj: IAddLaboratoryFile = {};

      laboratoryObj.id = this.editProblemsId;
      laboratoryObj.categoryId = this.laboratoryForm.value?.categoryValue;
      laboratoryObj.examTitleId = this.laboratoryForm.value?.examTitleValue;
      laboratoryObj.date = this.laboratoryForm.value?.dateValue;
      laboratoryObj.description = this.laboratoryForm.value?.descriptionValue;
      laboratoryObj.file = this.fileObj;

      if (this.isEdit) {
        this.laboratoryService.editLaboratory(laboratoryObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Laboratory edited successfully'
            );
            this.laboratoryService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to edit Laboratory');
            this.handleCancel();
          },
        });
      } else {
        this.laboratoryService.addLaboratory(laboratoryObj).subscribe({
          next: () => {
            this.swalToastService.toastSuccess('Laboratory added successfully');
            this.laboratoryService.getData();

            this.handleCancel();
          },
          error: (err) => {
            this.swalToastService.toastError('Unable to add Laboratory');
            this.handleCancel();
          },
        });
      }
    } else {
      this.isLoading = false;
    }
  }

  async onFileSelected(event: any) {
    const file: File | null = event.target.files[0];
    const result = await validateUpload(file);
    if (!result.ok) {
      this.swalToastService.toastError(result.error!);
      this.fileObj = undefined;
      this.laboratoryForm.get('fileValue')?.setValue(undefined);
      this.laboratoryForm?.get('fileValue')?.setErrors({ invalid: true });
      // Physically clear the native file input so the same file can be re-selected
      if (this.fileInput?.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      return;
    }
    this.fileObj = file!;
    this.laboratoryForm.get('fileValue')?.setValue(file?.name);
    this.laboratoryForm.get('fileValue')?.setErrors(null);
  }
}
